import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, createContext, useContext } from 'react';
import { useAuth } from '../App';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import HollyAssistant from './HollyAssistant';
import NotificationManager from './NotificationManager';
import NotificationBell from './NotificationBell';
import InstallPWA from './InstallPWA';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  Calendar, 
  CalendarX,
  FileText, 
  Building, 
  Upload, 
  Users, 
  LogOut,
  Star,
  Menu,
  X,
  BarChart3,
  TrendingUp,
  Settings,
  HardDrive,
  Key,
  UserCheck,
  DollarSign,
  ChevronDown,
  ChevronRight,
  Ticket,
  Video,
  FileSpreadsheet,
  BookOpen,
  Share2,
  ShieldCheck,
  Banknote,
  ClipboardCheck,
  PlaneLanding,
  Wallet,
  HandCoins,
  Headphones,
  Wrench
, Mail,
  Scale
} from 'lucide-react';

// Icon mapping from string to component
const iconMap = {
  LayoutDashboard,
  Calendar,
  CalendarX,
  FileText,
  Building,
  Upload,
  Users,
  Star,
  BarChart3,
  TrendingUp,
  Settings,
  HardDrive,
  Key,
  UserCheck,
  DollarSign,
  Ticket,
  Video,
  FileSpreadsheet,
  BookOpen,
  Share2,
  ShieldCheck,
  Banknote,
  ClipboardCheck,
  PlaneLanding,
  Wallet,
  HandCoins,
  Headphones,
  Wrench,
  Mail,
  Scale
};

const SPECIAL_ACCESS_ROLES = {
  '/cs-control': ['admin', 'invoice_operator'],
  '/admin/calendari-listini': ['admin', 'bookings_operator'],
  '/medio-termine': ['admin', 'bookings_operator'],
  '/preventivi': ['admin', 'customer_service', 'bookings_operator'],
  '/gestione-spese': ['admin', 'customer_service'],
  '/promozioni': ['admin', 'customer_service', 'bookings_operator'],
  '/newsletter': ['admin', 'marketing'],
  '/admin/accounting-control': ['admin', 'invoice_operator', 'customer_service', 'bookings_operator'],
  '/admin/home-smart-economics': ['admin'],
  '/admin/home-smart-events': ['admin'],
  '/admin/proposte-prezzi': ['admin'],
  '/admin/autonomia-hwsmart': ['admin'],
};

const ACCESS_GROUPS = {
  internal: new Set(['bookings_operator', 'customer_service', 'invoice_operator', 'marketing']),
  collaborator: new Set(['employee']),
  external: new Set(['property_responsible']),
};

const ACCESS_STYLES = {
  admin: {
    idle: 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300',
    active: 'bg-slate-100 border-slate-500 text-slate-950 ring-1 ring-slate-300 shadow-sm',
  },
  internal: {
    idle: 'bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100 hover:border-blue-300',
    active: 'bg-blue-700 border-blue-800 text-white ring-1 ring-blue-300 shadow-md',
  },
  collaborator: {
    idle: 'bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300',
    active: 'bg-emerald-700 border-emerald-800 text-white ring-1 ring-emerald-300 shadow-md',
  },
  external: {
    idle: 'bg-amber-50 border-amber-300 text-amber-950 hover:bg-amber-100 hover:border-amber-400',
    active: 'bg-amber-600 border-amber-700 text-white ring-1 ring-amber-300 shadow-md',
  },
};

const getMenuAccess = (item) => {
  const roles = [...new Set(SPECIAL_ACCESS_ROLES[item.path] || item.access_roles || item.roles || ['admin'])];
  const nonAdmin = roles.filter((role) => role !== 'admin');
  let group = 'admin';
  if (nonAdmin.some((role) => ACCESS_GROUPS.external.has(role))) group = 'external';
  else if (nonAdmin.some((role) => ACCESS_GROUPS.collaborator.has(role))) group = 'collaborator';
  else if (nonAdmin.length > 0) group = 'internal';
  const dots = [];
  if (nonAdmin.some((role) => ACCESS_GROUPS.internal.has(role))) dots.push({ key: 'internal', className: 'bg-blue-500', label: 'Interno' });
  if (nonAdmin.some((role) => ACCESS_GROUPS.collaborator.has(role))) dots.push({ key: 'collaborator', className: 'bg-emerald-500', label: 'Dipendenti/collaboratori' });
  if (nonAdmin.some((role) => ACCESS_GROUPS.external.has(role))) dots.push({ key: 'external', className: 'bg-amber-500', label: 'Esterno' });
  return { roles, group, dots };
};
// Context per prevenire Layout nidificati
const LayoutContext = createContext(false);

const Layout = ({ children, title }) => {
  // Se siamo già dentro un Layout, renderizza solo children (passa il title)
  const isInsideLayout = useContext(LayoutContext);
  if (isInsideLayout) {
    return <>{children}</>;
  }
  
  return (
    <LayoutContext.Provider value={true}>
      <LayoutInner title={title}>{children}</LayoutInner>
    </LayoutContext.Provider>
  );
};

const LayoutInner = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const isImpersonating = !!localStorage.getItem('hw_admin_token_backup');

  const handleReturnToAdmin = () => {
    const adminToken = localStorage.getItem('hw_admin_token_backup');
    if (adminToken) {
      localStorage.setItem('hw_token', adminToken);
      localStorage.removeItem('hw_admin_token_backup');
      window.location.href = '/dashboard';
    }
  };

  useEffect(() => {
    if (user?.role) {
      fetchMenuConfig();
    }
  }, [user?.role]);

  // Popup globale: fatture Booking rinviate con casa RIAPERTA (una volta per sessione)
  useEffect(() => {
    if (!['admin', 'customer_service'].includes(user?.role)) return;
    if (sessionStorage.getItem('hw_bi_alerts_shown')) return;
    sessionStorage.setItem('hw_bi_alerts_shown', '1');
    api.get('/booking-invoices/alerts').then(r => {
      const alerts = r.data?.alerts || [];
      if (alerts.length) {
        toast.warning(`🏠 ${alerts.length} fattur${alerts.length === 1 ? 'a' : 'e'} Booking rinviat${alerts.length === 1 ? 'a' : 'e'}: casa RIAPERTA — da rimettere in pagamento`, {
          duration: 20000,
          action: { label: 'Vai alle fatture', onClick: () => navigate('/xml-converter?tab=bkinvoices') },
        });
      }
    }).catch(() => {});
  }, [user?.role]);

  // Default menu fallback when API fails
  const getDefaultMenu = (role) => {
    if (role === 'admin') {
      return [
        { id: 'dashboard', label: 'Dashboard', path: '/admin/dashboard', icon: 'LayoutDashboard', access_roles: ['admin', 'bookings_operator', 'customer_service', 'invoice_operator', 'property_responsible'] },
        { id: 'properties', label: 'Proprietà', path: '/admin/properties', icon: 'Building', access_roles: ['admin', 'customer_service'] },
        { id: 'bookings', label: 'Prenotazioni', path: '/admin/bookings', icon: 'Calendar', access_roles: ['admin', 'bookings_operator', 'property_responsible'] },
        { id: 'xml-converter', label: 'Convertitore XML Bonifici', path: '/xml-converter', icon: 'FileText', access_roles: ['admin', 'customer_service'] },
        { id: 'cs-control', label: 'Performance Customer Service', path: '/cs-control', icon: 'Headphones', access_roles: ['admin', 'invoice_operator'] },
        { id: 'payment-archive', label: 'Archivio Pagamenti', path: '/payment-archive', icon: 'Archive', access_roles: ['admin', 'customer_service', 'bookings_operator', 'invoice_operator', 'marketing'] },
        { id: 'payment-contacts', label: 'Rubrica Pagamenti', path: '/payment-contacts', icon: 'Users', access_roles: ['admin', 'customer_service', 'bookings_operator', 'invoice_operator'] },
        { id: 'invoice-payment-archive', label: 'Archivio Pagamenti Fatture', path: '/invoice-payment-archive', icon: 'FileText', access_roles: ['admin', 'invoice_operator'] },
        { id: 'recurring-costs', label: 'Costi Fissi Ricorrenti', path: '/recurring-costs', icon: 'RefreshCw', access_roles: ['admin', 'invoice_operator'] },
        { id: 'hw-academy-pricing', label: 'Proposte prezzi Academy', path: '/admin/proposte-prezzi', icon: 'TrendingUp', access_roles: ['admin'] },
        { id: 'autonomia-hwsmart', label: 'Autonomia HWsmart', path: '/admin/autonomia-hwsmart', icon: 'ShieldCheck', access_roles: ['admin'] },
        { id: 'accounting-control', label: 'Controllo Gestione & Conti', path: '/admin/accounting-control', icon: 'Scale', access_roles: ['admin', 'invoice_operator', 'customer_service', 'bookings_operator'] },
        { id: 'home-smart-economics', label: 'Ricavi Home Smart', path: '/admin/home-smart-economics', icon: 'BarChart3', access_roles: ['admin'] },
      ];
    }
    return [
      { id: 'dashboard', label: 'Dashboard', path: `/${role}/dashboard`, icon: 'LayoutDashboard' },
    ];
  };

  const fetchMenuConfig = async () => {
    setLoading(true);
    // Voce Performance CS visibile anche a email specifiche non-admin
    const withCsPerformance = (items) => {
      const allowed = ['partnerinvoice@holidayw.eu'].includes((user?.email || '').toLowerCase());
      if (!allowed || (items || []).some(i => i.path === '/cs-control')) return items;
      return [...items, { id: 'cs-control', label: 'Performance Customer Service', path: '/cs-control', icon: 'Headphones', access_roles: ['admin', 'invoice_operator'] }];
    };
    // Voce Calendari Listini: admin + prenotazioni@
    const withPricelistCalendars = (items) => {
      const allowed = user?.role === 'admin' || (user?.email || '').toLowerCase() === 'prenotazioni@holidayw.eu';
      if (!allowed || (items || []).some(i => i.path === '/admin/calendari-listini')) return items;
      return [...items, { id: 'pricelist-calendars', label: 'Calendari Listini', path: '/admin/calendari-listini', icon: 'Calendar', access_roles: ['admin', 'bookings_operator'] }];
    };
    // Voce Medio Termine: admin + prenotazioni@
    const withMidTerm = (items) => {
      const allowed = user?.role === 'admin' || (user?.email || '').toLowerCase() === 'prenotazioni@holidayw.eu';
      if (!allowed || (items || []).some(i => i.path === '/medio-termine')) return items;
      return [...items, { id: 'mid-term', label: 'Medio Termine', path: '/medio-termine', icon: 'Calendar', access_roles: ['admin', 'bookings_operator'] }];
    };
    // Voce Preventivi: admin + customer service + prenotazioni
    const withPreventivi = (items) => {
      const allowed = ['admin', 'customer_service', 'bookings_operator'].includes(user?.role);
      if (!allowed || (items || []).some(i => i.path === '/preventivi')) return items;
      return [...items, { id: 'preventivi', label: 'Preventivi', path: '/preventivi', icon: 'FileText', access_roles: ['admin', 'customer_service', 'bookings_operator'] }];
    };
    // Voce Gestione Spese: admin + customer service
    const withGestioneSpese = (items) => {
      const allowed = ['admin', 'customer_service'].includes(user?.role);
      if (!allowed || (items || []).some(i => i.path === '/gestione-spese')) return items;
      return [...items, { id: 'gestione-spese', label: 'Gestione Spese', path: '/gestione-spese', icon: 'Receipt', access_roles: ['admin', 'customer_service'] }];
    };
    // Voce Promozioni: admin + customer service + prenotazioni
    const withPromozioni = (items) => {
      const allowed = ['admin', 'customer_service', 'bookings_operator'].includes(user?.role);
      if (!allowed || (items || []).some(i => i.path === '/promozioni')) return items;
      return [...items, { id: 'promozioni', label: 'Promozioni', path: '/promozioni', icon: 'Percent', access_roles: ['admin', 'customer_service', 'bookings_operator'] }];
    };
    // Voce Newsletter: admin + marketing
    const withNewsletter = (items) => {
      const allowed = ['admin', 'marketing'].includes(user?.role);
      if (!allowed || (items || []).some(i => i.path === '/newsletter')) return items;
      return [...items, { id: 'newsletter', label: 'Newsletter', path: '/newsletter', icon: 'Mail', access_roles: ['admin', 'marketing'] }];
    };
    const withAcademyPricing = (items) => {
      if (user?.role !== 'admin' || (items || []).some(i => i.path === '/admin/proposte-prezzi')) return items;
      return [...items, { id: 'hw-academy-pricing', label: 'Proposte prezzi Academy', path: '/admin/proposte-prezzi', icon: 'TrendingUp', access_roles: ['admin'] }];
    };
    const withAutonomy = (items) => {
      if (user?.role !== 'admin' || (items || []).some(i => i.path === '/admin/autonomia-hwsmart')) return items;
      return [...items, { id: 'autonomia-hwsmart', label: 'Autonomia HWsmart', path: '/admin/autonomia-hwsmart', icon: 'ShieldCheck', access_roles: ['admin'] }];
    };
    const withAccountingControl = (items) => {
      const allowed = ['admin', 'invoice_operator', 'customer_service', 'bookings_operator'].includes(user?.role);
      if (!allowed || (items || []).some(i => i.path === '/admin/accounting-control')) return items;
      return [...items, { id: 'accounting-control', label: 'Controllo Gestione & Conti', path: '/admin/accounting-control', icon: 'Scale', access_roles: ['admin', 'invoice_operator', 'customer_service', 'bookings_operator'] }];
    };
    const withHomeSmartEconomics = (items) => {
      if (user?.role !== 'admin' || (items || []).some(i => i.path === '/admin/home-smart-economics')) return items;
      return [...items, { id: 'home-smart-economics', label: 'Ricavi Home Smart', path: '/admin/home-smart-economics', icon: 'BarChart3', access_roles: ['admin'] }];
    };
    const withHomeSmartEvents = (items) => {
      if (user?.role !== 'admin' || (items || []).some(i => i.path === '/admin/home-smart-events')) return items;
      return [...items, { id: 'home-smart-events', label: 'Eventi Home Smart', path: '/admin/home-smart-events', icon: 'CalendarDays', access_roles: ['admin'] }];
    };
    const withExtras = (items) => withHomeSmartEvents(withHomeSmartEconomics(withAccountingControl(withAutonomy(withAcademyPricing(withNewsletter(withPromozioni(withGestioneSpese(withPreventivi(withMidTerm(withPricelistCalendars(withCsPerformance(items))))))))))));
    try {
      const response = await api.get(`/menu-config/role/${user.role}`);
      const menuStructure = response.data.menu_structure || [];
      if (menuStructure.length > 0) {
        setMenuItems(withExtras(menuStructure));
      } else {
        // API returned empty menu, use fallback
        console.log('⚠️ Empty menu from API, using fallback');
        setMenuItems(withExtras(getDefaultMenu(user.role)));
      }
    } catch (error) {
      console.error('❌ Error fetching menu config after retries:', error);
      // Fallback to default menu if API fails after all retries
      console.log('📋 Using default menu fallback');
      setMenuItems(withExtras(getDefaultMenu(user.role)));
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getParentItems = () => menuItems.filter(item => !item.parent_id);
  const getChildItems = (parentId) => menuItems.filter(item => item.parent_id === parentId);

  // Auto-expand parent when on its page or a child page
  useEffect(() => {
    const parents = getParentItems();
    const toExpand = [];
    for (const parent of parents) {
      const children = getChildItems(parent.id);
      if (children.length > 0) {
        const isParentActive = location.pathname === parent.path;
        const isChildActive = children.some(c => location.pathname === c.path);
        if (isParentActive || isChildActive) {
          toExpand.push(parent.id);
        }
      }
    }
    if (toExpand.length > 0) {
      setExpandedItems(prev => [...new Set([...prev, ...toExpand])]);
    }
  }, [location.pathname, menuItems]);

  const renderMenuItem = (item, isChild = false) => {
    const Icon = iconMap[item.icon] || LayoutDashboard;
    const isActive = location.pathname === item.path;
    const children = getChildItems(item.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const access = getMenuAccess(item);
    const accessStyle = ACCESS_STYLES[access.group];
    const roleLabel = access.roles.length === 1 ? 'Solo admin' : `Condiviso con: ${access.roles.filter(role => role !== 'admin').map(role => role.replaceAll('_', ' ')).join(', ')}`;

    return (
      <div key={item.id}>
        <div className="relative">
          <Button
            asChild
            variant="ghost"
            className={`justify-start h-auto min-h-12 py-2.5 border ${isActive ? accessStyle.active : accessStyle.idle} ${isChild ? 'ml-4 w-[calc(100%-1rem)] min-h-10' : 'w-full'} ${hasChildren ? 'pr-10' : ''}`}
            data-testid={`nav-${item.label.toLowerCase().replaceAll(' ', '-')}`}
          >
            <Link
              to={item.path}
              title={item.label}
              onClick={(e) => {
                // Click modificato (ctrl/cmd/shift o tasto centrale) → lascia che
                // il browser apra in una nuova scheda, senza eseguire side-effect.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                if (hasChildren) toggleExpand(item.id);
                setSidebarOpen(false);
              }}
            >
              <Icon className="mr-3 h-5 w-5 shrink-0" />
              <span className="min-w-0 flex-1 whitespace-normal break-words text-left leading-snug">{item.label}</span>
              <span className={`ml-2 flex shrink-0 gap-1 ${hasChildren ? 'mr-5' : ''}`} title={roleLabel} data-testid={`nav-access-${item.id}`}>
                {access.group === 'admin' ? <span className="h-2.5 w-2.5 rounded-full border border-slate-400 bg-white" aria-label="Solo admin" /> : access.dots.map(dot => <span key={dot.key} className={`h-2.5 w-2.5 rounded-full ${dot.className}`} aria-label={dot.label} />)}
              </span>
            </Link>
          </Button>
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(item.id);
              }}
              className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded hover:bg-slate-200 ${
                isActive && access.group !== 'admin' ? 'text-white hover:bg-black/10' : 'text-slate-600'
              }`}
              data-testid={`nav-expand-${item.id}`}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {children.map(child => renderMenuItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-80 max-w-[calc(100vw-1rem)] bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`} data-testid="desktop-sidebar">
        <div className="flex flex-col h-full">
          {/* Logo and close button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="hw-logo hw-logo-small">
              <img src="/1.png" alt="HW Smart Logo" />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="sidebar-close-button"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold">
                {user?.full_name?.charAt(0)}
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">{user?.full_name}</div>
                <div className="text-slate-500 text-xs capitalize">{user?.role?.replace('_', ' ')}</div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto pb-24">
            {user?.role === 'admin' && (
              <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid="menu-access-legend">
                <div className="text-[10px] font-bold uppercase text-slate-500 mb-2">Visibilità menu</div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-[10px] text-slate-700">
                  <span className="flex items-center gap-1.5" data-testid="legend-admin-only"><span className="h-2.5 w-2.5 rounded-full border border-slate-400 bg-white" />Solo admin</span>
                  <span className="flex items-center gap-1.5" data-testid="legend-internal"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />Interno</span>
                  <span className="flex items-center gap-1.5" data-testid="legend-collaborator"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Collaboratori</span>
                  <span className="flex items-center gap-1.5" data-testid="legend-external"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Esterni</span>
                </div>
              </div>
            )}
            {loading ? (
              <div className="text-center text-slate-500 py-4">Caricamento menu...</div>
            ) : (
              getParentItems().map((item) => renderMenuItem(item))
            )}
            
            {/* Guida operativa PDF per l'ufficio */}
            {['admin', 'customer_service', 'bookings_operator'].includes(user?.role) && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/static/downloads/guida_operativa_ufficio_settembre2026.pdf`}
                target="_blank" rel="noreferrer" data-testid="nav-guida-ufficio"
                className="flex items-center w-full h-12 px-4 rounded-md border border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100 hover:border-blue-300 text-sm font-medium"
              >
                <BookOpen className="mr-3 h-5 w-5" />
                <span className="truncate">Guida Ufficio (PDF)</span>
                <span className="ml-auto h-2.5 w-2.5 rounded-full bg-blue-500" title="Condiviso con ruoli interni" aria-label="Interno" />
              </a>
            )}

            {/* Logout button inside navigation to avoid being covered */}
            <div className="pt-4 mt-4 border-t border-slate-200">
              <Button
                variant="ghost"
                className="w-full justify-start h-12 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Esci
              </Button>
            </div>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="md:ml-80">
        {/* Impersonation banner */}
        {isImpersonating && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between" data-testid="impersonation-banner">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="h-4 w-4" />
              <span>Stai visualizzando come: <strong>{user?.full_name || user?.email}</strong></span>
            </div>
            <Button
              size="sm"
              onClick={handleReturnToAdmin}
              className="bg-white text-amber-700 hover:bg-amber-50 font-semibold text-xs h-7"
              data-testid="return-to-admin-btn"
            >
              Torna ad Admin
            </Button>
          </div>
        )}
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-slate-200">
          <div className="flex min-w-0 items-center justify-between px-4 py-3">
            <div className="flex min-w-0 items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                className="md:hidden bg-white shadow-md border-slate-300 hover:bg-slate-50 px-3 py-1.5 rounded-lg"
                onClick={() => setSidebarOpen(true)}
                data-testid="admin-menu-toggle"
              >
                <Menu className="h-5 w-5 mr-1" />
                <span className="text-sm font-medium">Menu</span>
              </Button>
              <h1 className="hidden truncate text-xl font-bold text-slate-900 sm:block" data-testid="page-title">{title}</h1>
            </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              {user?.role === 'admin' && (
                <div className="hidden xl:flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900" data-testid="cutover-legacy-status-badge">
                  <ShieldCheck className="h-4 w-4" /> API legacy · nessun cutover
                </div>
              )}
              {/* Install PWA Button */}
              <InstallPWA />
              {/* Notification Bell */}
              <NotificationBell />
              {/* Notification Manager */}
              <NotificationManager />
              <div className="hidden text-sm text-slate-500 xl:block">
                {new Date().toLocaleDateString('it-IT', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-2">
          {children}
        </main>
      </div>
      
      {/* Evita overlay sui workflow amministrativi critici */}
      {!location.pathname.startsWith('/admin/autonomia-hwsmart') && !location.pathname.startsWith('/admin/accounting-control') && !location.pathname.startsWith('/admin/istat/connessioni') && <HollyAssistant />}
    </div>
  );
};

export default Layout;